import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import apiClient from '../../utils/apiClient';

const fmtUSD = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtInt = (n) => (n ?? 0).toLocaleString();

const StatCard = ({ icon: Icon, label, value, sublabel, tone = 'emerald' }) => {
  const tones = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600' },
    rose: { bg: 'bg-rose-50', icon: 'text-rose-600' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600' },
  };
  const t = tones[tone];
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.bg}`}>
          <Icon className={`h-4 w-4 ${t.icon}`} />
        </div>
      </div>
      <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{value ?? '—'}</p>
      {sublabel && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{sublabel}</p>}
    </div>
  );
};

const PLAN_COLORS = {
  free: '#94a3b8',
  starter: '#60a5fa',
  pro: '#10b981',
  enterprise: '#a855f7',
};

const StatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/platform/stats');
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading && !stats) {
    return <div className="p-8 text-center text-[13px] text-slate-500">Loading stats…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] rounded-lg px-4 py-3">
        {error}
      </div>
    );
  }
  if (!stats) return null;

  const planMixData = Object.entries(stats.plan_mix || {}).map(([plan, data]) => ({
    plan,
    count: data.count,
    mrr: data.mrr,
    color: PLAN_COLORS[plan] || '#64748b',
  }));

  const statusData = Object.entries(stats.status_counts || {})
    .filter(([, n]) => n > 0)
    .map(([status, count]) => ({ status, count }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={DollarSign} label="MRR" value={fmtUSD(stats.mrr)} sublabel={`ARR ${fmtUSD(stats.arr)}`} tone="emerald" />
        <StatCard icon={TrendingUp} label="New orgs 30d" value={fmtInt(stats.new_orgs_30d)} sublabel={`${stats.new_orgs_7d} this week`} tone="blue" />
        <StatCard icon={Sparkles} label="Active trials" value={fmtInt(stats.active_trials)} sublabel={`${stats.trials_ending_soon} ending <7d`} tone="purple" />
        <StatCard icon={AlertTriangle} label="Canceled 30d" value={fmtInt(stats.canceled_30d)} tone="rose" />
        <StatCard icon={Users} label="New customers 30d" value={fmtInt(stats.new_customers_30d)} tone="amber" />
        <StatCard icon={Clock} label="New rentals 30d" value={fmtInt(stats.new_rentals_30d)} tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <h3 className="text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-3">MRR by plan</h3>
          {planMixData.length === 0 ? (
            <p className="text-[12px] text-slate-400 dark:text-slate-500">No paying orgs yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={planMixData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="plan" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtUSD(v)} />
                <Bar dataKey="mrr" radius={[6, 6, 0, 0]}>
                  {planMixData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <h3 className="text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-3">Status mix</h3>
          {statusData.length === 0 ? (
            <p className="text-[12px] text-slate-400 dark:text-slate-500">No organizations yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusData} dataKey="count" nameKey="status" outerRadius={70} label>
                  {statusData.map((e, i) => (
                    <Cell key={i} fill={['#10b981', '#60a5fa', '#f59e0b', '#f43f5e', '#94a3b8'][i % 5]} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200/80 dark:border-slate-800">
          <h3 className="text-[14px] font-semibold text-slate-900 dark:text-slate-100">Top organizations by customer count</h3>
        </div>
        {(!stats.top_orgs || stats.top_orgs.length === 0) ? (
          <div className="p-8 text-center text-[13px] text-slate-500">No organizations yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Organization</th>
                  <th className="px-4 py-2 text-left font-semibold">Plan</th>
                  <th className="px-4 py-2 text-left font-semibold">Status</th>
                  <th className="px-4 py-2 text-right font-semibold">MRR</th>
                  <th className="px-4 py-2 text-right font-semibold">Customers</th>
                  <th className="px-4 py-2 text-right font-semibold">Rentals</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {stats.top_orgs.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">{o.name}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 uppercase text-[11px]">{o.plan}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 text-[11px]">{o.status}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{fmtUSD(o.monthly_amount)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{fmtInt(o.customers)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">{fmtInt(o.rentals)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPanel;
